import { useState } from "react";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import Swal from "sweetalert2";

import { AsyncPaginate } from "react-select-async-paginate";

import DashboardLayout from "../../../../components/layout/DashboardLayout";

import { useCustomerViewModel } from "../../../../viewmodels/operation/customer/useCustomerViewModel";

import { useCustomerScaleViewModel } from "../../../../viewmodels/operation/scale/useCustomerScaleViewModel";

import CreateScaleModal from "../../scale/scale/CreateScaleModal";

import UpdateScaleModal from "./UpdateScaleModal";

import toast from "react-hot-toast";

const CustomerScalePage = () => {
  const [exportLoading, setExportLoading] = useState(false);

  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const [editingScale, setEditingScale] = useState<any>(null);

  const [customerSearchText, setCustomerSearchText] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);

  const { loadCustomerOptions } = useCustomerViewModel();

  const {
    scales,
    loading,
    loadScales,
    clearScales,
    deleteScale,
    updateNotes,
    createCalibrationReports,
  } = useCustomerScaleViewModel();

  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const [selectedScaleIds, setSelectedScaleIds] = useState<string[]>([]);
  const [creatingCalibrationReports, setCreatingCalibrationReports] =
    useState(false);

  const [lastCreatedCustomer, setLastCreatedCustomer] = useState<any>(null);

  const toggleScaleSelection = (scaleId: string) => {
    setSelectedScaleIds((prev) =>
      prev.includes(scaleId)
        ? prev.filter((id) => id !== scaleId)
        : [...prev, scaleId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedScaleIds.length === scales.length) {
      setSelectedScaleIds([]);
      return;
    }

    setSelectedScaleIds(
      scales.map((scale: any) => scale.id).filter((id: string) => !!id),
    );
  };

  const handleCreateCalibrationReports = async () => {
    if (selectedScaleIds.length === 0) {
      toast.error("Please select at least one scale.");
      return;
    }

    try {
      setCreatingCalibrationReports(true);

      const success = await createCalibrationReports(selectedScaleIds);

      if (success) {
        setSelectedScaleIds([]);

        if (selectedCustomer?.value) {
          await loadScales(selectedCustomer.value);
        }
      }
    } finally {
      setCreatingCalibrationReports(false);
    }
  };

  const generatePdf = async () => {
    if (!selectedCustomer) return;

    try {
      setExportLoading(true);

      const doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const drawHeader = () => {
        doc.addImage("/logo.png", "PNG", 10, 8, 43, 18);

        doc.setFontSize(9);

        doc.text(
          [
            "ABN : 94063128844",
            "2/22, MICHELLAN COURT, BAYSWATER, VIC 3132",
            "Australia wide - 1300 669 996",
            "sales@universalweighing.com.au",
            "www.universalweighing.com.au",
          ],
          pageWidth - 10,
          10,
          { align: "right" },
        );

        doc.line(10, 30, pageWidth - 10, 30);
      };

      const drawFooter = () => {
        const y = pageHeight - 20;

        doc.line(10, y, pageWidth - 10, y);

        doc.addImage("/dano.png", "PNG", 10, y + 2, 20, 15);

        doc.addImage("/uwqs.jpg", "JPEG", pageWidth / 2 - 10, y + 2, 15, 15);

        doc.addImage("/wiaa.jpg", "JPEG", pageWidth - 35, y + 2, 25, 15);

        doc.setFontSize(8);
      };

      autoTable(doc, {
        startY: 58,
        head: [
          [
            "Id",
            "Make",
            "Model",
            "Capacity",
            "Serial No",
            "Location",
            "Comment",
          ],
        ],
        body: scales.map((x: any) => [
          x.identifier ?? "",
          x.makeName ?? "",
          x.modelName ?? "",
          x.capacity ?? "",
          x.serialNumber ?? "",
          x.location ?? "",
          x.comment ?? "",
        ]),
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 45, halign: "left" },
        },
        margin: {
          top: 55,
          bottom: 25,
          left: 10,
          right: 10,
        },
        didDrawPage: () => {
          drawHeader();

          if (doc.getCurrentPageInfo().pageNumber === 1) {
            doc.setFontSize(16);
            doc.text("Customer Scale List", pageWidth / 2, 40, {
              align: "center",
            });

            doc.setFontSize(10);
            doc.text(selectedCustomer.label, pageWidth / 2, 48, {
              align: "center",
            });

            doc.setFontSize(9);
            doc.text(scales?.[0]?.addressLine1 || "", pageWidth / 2, 54, {
              align: "center",
              maxWidth: 500,
            });
          }

          drawFooter();
        },
      });

      doc.save(`CustomerScaleReport-${selectedCustomer.label}.pdf`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Scale?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    await deleteScale(id, selectedCustomer.value);
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold">Scales</h1>

            <p className="text-gray-500 mt-1">Manage customer scales</p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-white px-5 py-3 rounded-2xl"
          >
            Create
          </button>
        </div>

        {/* FILTER */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* CUSTOMER */}
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Customer</label>

              <div className="relative">
                <AsyncPaginate
                  value={selectedCustomer}
                  loadOptions={loadCustomerOptions}
                  additional={{ page: 1 }}
                  placeholder="Search Customer"
                  inputValue={customerSearchText}
                  onInputChange={(value) => {
                    setCustomerSearchText(value);
                    return value;
                  }}
                  onChange={async (selected: any) => {
                    setSelectedCustomer({
                      ...selected,

                      addressLine1:
                        selected?.data?.addresses?.[0]?.addressLine1 ||
                        selected?.data?.addressLine1 ||
                        "",
                    });

                    if (selected?.value) {
                      await loadScales(selected.value);
                    } else {
                      clearScales();
                    }
                  }}
                />

                {selectedCustomer && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomer(null);
                      clearScales();
                    }}
                    className="absolute right-10 top-1/2 -translate-y-1/2 z-10
                 w-6 h-6 rounded-full
                 bg-red-100 text-red-600
                 hover:bg-red-200
                 flex items-center justify-center
                 text-sm font-bold"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* BUTTONS */}
            <div className="flex items-end gap-3">
              <button
                onClick={generatePdf}
                disabled={
                  !selectedCustomer || scales.length === 0 || exportLoading
                }
                className="h-[42px] bg-green-600 text-white px-6 rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exportLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Exporting...
                  </>
                ) : (
                  "Export PDF"
                )}
              </button>
            </div>
          </div>

          {/* SELECTED CUSTOMER */}
          {selectedCustomer && (
            <div className="mt-3 text-sm text-slate-500">
              Selected Customer:
              <span className="font-semibold ml-1 text-slate-700">
                {selectedCustomer.label}
              </span>
            </div>
          )}
        </div>

        {/* TABLE */}

        <div className="bg-white rounded-xl shadow border border-slate-300 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border p-3 text-center w-12">
                    <input
                      type="checkbox"
                      checked={
                        scales.length > 0 &&
                        selectedScaleIds.length === scales.length
                      }
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>

                  <th className="border p-3 text-left">Id</th>

                  <th className="border p-3 text-left">Make</th>

                  <th className="border p-3 text-left">Model</th>

                  <th className="border p-3 text-left">Capacity</th>

                  <th className="border p-3 text-left">Serial No</th>

                  <th className="border p-3 text-left">Location</th>

                  <th className="border p-3 text-left">Comment</th>

                  <th className="border p-3 text-left">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="p-6 text-center">
                      Loading...
                    </td>
                  </tr>
                ) : scales.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-6 text-center">
                      No scales found
                    </td>
                  </tr>
                ) : (
                  scales.map((x: any) => (
                    <tr key={x.id} className="hover:bg-slate-50">
                      <td className="border p-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedScaleIds.includes(x.id)}
                          onChange={() => toggleScaleSelection(x.id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>

                      <td className="border p-3">{x.identifier ?? "-"}</td>

                      <td className="border p-3">{x.makeName ?? "-"}</td>

                      <td className="border p-3">{x.modelName ?? "-"}</td>

                      <td className="border p-3">{x.capacity ?? "-"}</td>

                      <td className="border p-3">{x.serialNumber ?? "-"}</td>

                      <td className="border p-3">{x.location ?? "-"}</td>

                      <td className="border p-3">{x.comment ?? "-"}</td>

                      <td className="border p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingScale(x);
                              setShowUpdateModal(true);
                            }}
                            className="bg-yellow-500 text-white px-3 py-2 rounded-lg"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(x.id)}
                            className="bg-red-500 text-white px-3 py-2 rounded-lg"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {selectedScaleIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <button
            type="button"
            onClick={handleCreateCalibrationReports}
            disabled={creatingCalibrationReports}
            className="
        bg-primary
        hover:opacity-90
        disabled:opacity-50
        disabled:cursor-not-allowed
        text-white
        px-6
        py-3
        rounded-2xl
        shadow-xl
        font-semibold
        flex
        items-center
        gap-3
        whitespace-nowrap
      "
          >
            {creatingCalibrationReports ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />

                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                Creating...
              </>
            ) : (
              <>
                Create Calibration Reports
                <span className="bg-white/20 px-2 py-1 rounded-lg text-sm">
                  {selectedScaleIds.length}
                </span>
              </>
            )}
          </button>
        </div>
      )}
      {showCreateModal && (
        <CreateScaleModal
          customerId={selectedCustomer?.value}
          customerName={
            selectedCustomer?.customerName ||
            selectedCustomer?.data?.name ||
            selectedCustomer?.label ||
            customerSearchText
          }
          onCancel={() => {
            if (lastCreatedCustomer) {
              setSelectedCustomer({
                value: lastCreatedCustomer.customerId,

                label: lastCreatedCustomer.customerName,
              });

              loadScales(lastCreatedCustomer.customerId);
            }

            setShowCreateModal(false);
          }}
          onSuccess={async (result) => {
            setLastCreatedCustomer(result);

            if (result?.customerId) {
              setSelectedCustomer({
                value: result.customerId,
                label: result.customerName,
              });

              await loadScales(result.customerId);
            }
          }}
        />
      )}

      <UpdateScaleModal
        open={showUpdateModal}
        scale={editingScale}
        customerId={selectedCustomer?.value}
        onClose={() => {
          setShowUpdateModal(false);
          setEditingScale(null);
        }}
        onSave={updateNotes}
      />
    </DashboardLayout>
  );
};

export default CustomerScalePage;
